import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { getItemDerivedStatus } from '@/lib/bulk-jobs/status'
import type { BulkJobItemDerivedStatus } from '@/lib/bulk-jobs/types'
import { supabase } from '@/lib/supabase/client'

// Re-export shared type for backwards compatibility
export type { BulkJobItemDerivedStatus as StpBulkJobItemDerivedStatus }

// =============================================================================
// STP Bulk Job Item Types
// =============================================================================

type StpBulkJobItemRow =
  Database['public']['Tables']['stp_bulk_job_items']['Row']

/**
 * STP Bulk job item with product and generation details
 * Status is derived from product.current_generation.status
 */
export type StpBulkJobItem = StpBulkJobItemRow & {
  products: {
    id: string
    name: string
    main_group: string | null
    sub_group: string | null
    category: string | null
    current_generation_id: string | null
    generations: {
      id: string
      status: string
      storage_path: string | null
    } | null
  } | null
}

/**
 * Derive the status of a bulk job item from its product/generation.
 * Delegates to shared logic with STP-specific field mapping.
 */
export function getStpBulkJobItemStatus(
  item: StpBulkJobItem
): BulkJobItemDerivedStatus {
  return getItemDerivedStatus({
    error_message: item.error_message,
    result_entity_id: item.product_id,
    generation_status: item.products?.generations?.status ?? null,
  })
}

// =============================================================================
// STP Bulk Job Item Queries
// =============================================================================

export const stpBulkJobItem = {
  findManyByJob: (jobId: string) =>
    queryOptions({
      queryKey: ['stp-bulk-job-items', 'job', jobId],
      queryFn: async (): Promise<StpBulkJobItem[]> => {
        if (!jobId || jobId.trim() === '') {
          throw new Error(
            'Invalid jobId: cannot fetch STP bulk job items without valid job ID'
          )
        }

        const { data, error } = await supabase
          .from('stp_bulk_job_items')
          .select(
            `
            *,
            products!product_id(
              id,
              name,
              main_group,
              sub_group,
              category,
              current_generation_id,
              generations!current_generation_id(
                id,
                status,
                storage_path
              )
            )
          `
          )
          .eq('bulk_job_id', jobId)
          .order('created_at', { ascending: true })

        if (error) {
          throw new Error(
            `Failed to fetch STP bulk job items: ${error.message}`,
            { cause: error }
          )
        }

        return (data || []) as StpBulkJobItem[]
      },
      staleTime: 10 * 1000,
    }),

  findCompletedByJob: (jobId: string) =>
    queryOptions({
      queryKey: ['stp-bulk-job-items', 'job', jobId, 'completed'],
      queryFn: async (): Promise<StpBulkJobItem[]> => {
        if (!jobId || jobId.trim() === '') {
          throw new Error(
            'Invalid jobId: cannot fetch STP bulk job items without valid job ID'
          )
        }

        // Get items that have a product with a completed generation
        const { data, error } = await supabase
          .from('stp_bulk_job_items')
          .select(
            `
            *,
            products!product_id(
              id,
              name,
              main_group,
              sub_group,
              category,
              current_generation_id,
              generations!current_generation_id(
                id,
                status,
                storage_path
              )
            )
          `
          )
          .eq('bulk_job_id', jobId)
          .not('product_id', 'is', null)
          .order('article_number', { ascending: true })

        if (error) {
          throw new Error(
            `Failed to fetch completed STP bulk job items: ${error.message}`,
            { cause: error }
          )
        }

        // Filter to only completed generations
        const completedItems = (data || []).filter((item) => {
          const typedItem = item as StpBulkJobItem
          return typedItem.products?.generations?.status === 'completed'
        })

        return completedItems as StpBulkJobItem[]
      },
      staleTime: 30 * 1000,
    }),
}
